import PubSub from "pubsub-js-ssr";

import type {
  Envelope,
  IMessageBus,
  MessageBusHistory,
  PickStartsWith,
  PublishEnvelope,
  PublishOptions,
  Signatures,
} from "./types";

export class MessageBus implements IMessageBus {
  version = 2;
  private history: MessageBusHistory = {};

  /**
   * Subscribe to channel specified in Signatures (`./types.ts`), `*` is wildcard for all channels or part of channel.
   * Example:
   * Assuming that we have topic defined as
   * - `checkout.cart.add`
   * - `checkout.cart.remove`
   * - `checkout.freeTrial.add`
   * - `user-profile.update`
   * in case of subscribing to `checkout.cart` we will receive messages from `checkout.cart.add` and `checkout.cart.remove`
   * in case of subscribing to `checkout` we will receive messages from `checkout.cart.add`, `checkout.cart.remove` and `checkout.freeTrial.add`
   * in case of subscribing to `*` we will receive messages from all topics
   *
   * @param request
   * @returns subscription id
   */
  subscribe<
    Channel extends string,
    PayloadRecord extends (Channel extends "*" ? Signatures : PickStartsWith<Signatures, Channel>),
  >(request: { channel: Channel; callback(response: Envelope<PayloadRecord>): void }): string {
    const result = this.getLastHistory(request.channel as keyof Signatures);
    if (result) {
      request.callback(result as Envelope<PayloadRecord>);
    }

    return PubSub.subscribe(request.channel, (_channel: string, data: Envelope<PayloadRecord>) => {
      request.callback(data);
    });
  }

  /**
   * Publishes message to channel specified in Signatures (`./types.ts`)
   * @param channel defined in Signatures (`./types.ts`)
   * @param envelop data to send to client
   * @param envelop.headers can be used to add additional information to message. e.g. `envelop.headers = { "source": "user-profile" }`
   * @param options
   * @param options.sticky if set to true, message will be stored in history and will be send to new subscribers
   */
  publish<Channel extends keyof Signatures>(
    channel: Channel,
    envelop: PublishEnvelope<Signatures, Channel>,
    { sticky }: PublishOptions = { sticky: false }
  ): void {
    const data: Envelope<Signatures, Channel> = {
      channel: channel,
      headers: envelop.headers,
      timestamp: Date.now(),
      payload: envelop.payload,
    };
    PubSub.publish(channel, data);
    if (sticky || this.history[channel] !== undefined) {
      Object.assign(this.history, { [channel]: data });
    }
  }

  /**
   * Removes subscription from channel specified in Signatures (`./types.ts`)
   * @param channel defined in Signatures (`./types.ts`)
   */
  unsubscribe<Channel extends keyof Signatures>(channel: Channel): string | boolean {
    return PubSub.unsubscribe(channel);
  }

  /**
   * Removes a single subscription by the token returned from {@link subscribe}.
   * Prefer this over {@link unsubscribe} when multiple consumers share a channel —
   * passing a topic string kills every subscriber on that topic.
   */
  unsubscribeToken(token: string): string | boolean {
    return PubSub.unsubscribe(token);
  }

  getHistory(): MessageBusHistory {
    return this.history;
  }

  removeHistoryItem<Topic extends keyof Signatures>(channel: Topic): void {
    if (this.history[channel]) {
      delete this.history[channel];
    }
  }

  getLastHistory<Topic extends keyof Signatures>(channel: Topic): Envelope<Signatures, Topic> | undefined {
    const value = this.history[channel];
    if (value?.channel === channel) {
      return value;
    }
    return undefined;
  }

  getVersion(): number {
    return this.version;
  }

  // drops all sticky history and every live subscription
  reset(): void {
    this.history = {};
    PubSub.clearAllSubscriptions();
  }
}
